import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Pencil, X } from "lucide-react";
import { membershipApi } from "../api";
import {
  useMember,
  useMemberContacts,
  useMemberDocuments,
  useMemberFees,
  useMemberIctAccess,
  useMembershipCategories,
  useMembershipFeeTypes,
  useTradeFollowUps,
} from "../hooks";
import { MEMBERSHIP_PERMISSIONS } from "../permissions";
import type { MemberStatus, MembershipSource } from "../types";
import { MembershipAccess } from "../components/MembershipAccess";
import { MembershipApprovalPanel } from "../components/MembershipApprovalPanel";
import { MemberContactsPanel } from "../components/MemberContactsPanel";
import { MembershipDocumentsPanel } from "../components/MembershipDocumentsPanel";
import { MembershipFinancePanel } from "../components/MembershipFinancePanel";
import { MembershipIctPanel } from "../components/MembershipIctPanel";
import { MembershipPageHeader } from "../components/MembershipPageHeader";
import { MembershipStatusBadge } from "../components/MembershipStatusBadge";
import { MembershipTradeFollowUpPanel } from "../components/MembershipTradeFollowUpPanel";
import { MembershipWorkflowTimeline } from "../components/MembershipWorkflowTimeline";

// ── Shared style constants ────────────────────────────────────────────────────

const inputCls =
  "w-full rounded-2xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100";

const selectCls =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100";

// ── Enum option lists ─────────────────────────────────────────────────────────

const SOURCE_OPTIONS: { label: string; value: MembershipSource }[] = [
  { label: "Website", value: "WEBSITE" },
  { label: "Email", value: "EMAIL" },
  { label: "Phone Call", value: "PHONE_CALL" },
  { label: "Walk-in", value: "WALK_IN" },
  { label: "Referral", value: "REFERRAL" },
  { label: "Bulk Upload", value: "BULK_UPLOAD" },
  { label: "Existing Member Import", value: "EXISTING_MEMBER_IMPORT" },
  { label: "Trade Team", value: "TRADE_TEAM" },
  { label: "Other", value: "OTHER" },
];

const STATUS_OPTIONS: { label: string; value: MemberStatus }[] = [
  { label: "Prospect", value: "PROSPECT" },
  { label: "Pending Review", value: "PENDING_REVIEW" },
  { label: "Board Pending", value: "BOARD_PENDING" },
  { label: "Board Approved", value: "BOARD_APPROVED" },
  { label: "Board Rejected", value: "BOARD_REJECTED" },
  { label: "Payment Pending", value: "PAYMENT_PENDING" },
  { label: "Payment Confirmed", value: "PAYMENT_CONFIRMED" },
  { label: "ICT Pending", value: "ICT_PENDING" },
  { label: "ICT Completed", value: "ICT_COMPLETED" },
  { label: "Training Pending", value: "TRAINING_PENDING" },
  { label: "Training Completed", value: "TRAINING_COMPLETED" },
  { label: "Active Member", value: "ACTIVE_MEMBER" },
  { label: "Suspended", value: "SUSPENDED" },
  { label: "Inactive", value: "INACTIVE" },
];

// ── Inline edit form state ────────────────────────────────────────────────────

interface InlineForm {
  companyName: string;
  categoryId: number | "";
  membershipCode: string;
  source: MembershipSource | "";
  status: MemberStatus | "";
  certificateIssued: boolean;
  notes: string;
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function MemberDetailsPage() {
  const { memberId } = useParams();
  const id = Number(memberId);

  const memberQuery = useMember(id);
  const contactsQuery = useMemberContacts(id);
  const feesQuery = useMemberFees(id);
  const documentsQuery = useMemberDocuments(id);
  const ictQuery = useMemberIctAccess(id);
  const followUpsQuery = useTradeFollowUps(id);
  const feeTypesQuery = useMembershipFeeTypes();
  const categoriesQuery = useMembershipCategories();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<InlineForm>({
    companyName: "",
    categoryId: "",
    membershipCode: "",
    source: "",
    status: "",
    certificateIssued: false,
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const member = memberQuery.data;

  // Populate form when member loads or edit mode opens
  useEffect(() => {
    if (member && editing) {
      setForm({
        companyName: member.companyName ?? "",
        categoryId: member.categoryId ?? "",
        membershipCode: member.membershipCode ?? "",
        source: member.source ?? "",
        status: member.status ?? "",
        certificateIssued: member.certificateIssued ?? false,
        notes: member.notes ?? "",
      });
      setSaveError("");
    }
  }, [member, editing]);

  const set = <K extends keyof InlineForm>(key: K, value: InlineForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const saveEdit = async () => {
    if (!form.companyName.trim()) {
      setSaveError("Company name is required.");
      return;
    }
    if (!form.categoryId) {
      setSaveError("Category is required.");
      return;
    }
    setSaveError("");
    setSaving(true);
    try {
      await membershipApi.updateMember(id, {
        companyName: form.companyName.trim(),
        categoryId: Number(form.categoryId),
        membershipCode: form.membershipCode || undefined,
        source: form.source || undefined,
        status: form.status || undefined,
        certificateIssued: form.certificateIssued,
        notes: form.notes || undefined,
      });
      await memberQuery.refetch();
      setEditing(false);
    } catch {
      setSaveError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const reload = async () => {
    await Promise.all([
      memberQuery.refetch(),
      contactsQuery.refetch(),
      feesQuery.refetch(),
      documentsQuery.refetch(),
      ictQuery.refetch(),
      followUpsQuery.refetch(),
    ]);
  };

  if (memberQuery.isLoading) {
    return <div className="p-6 text-sm text-slate-500">Loading member…</div>;
  }

  if (!member) {
    return <div className="p-6 text-sm text-red-600">Member not found.</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <MembershipPageHeader
        title={member.companyName}
        description={`Code: ${member.membershipCode ?? "Not assigned"}  ·  Category: ${member.categoryName}  ·  Stage: ${member.workflowStage?.replace(/_/g, " ") ?? "—"}`}
        actions={
          <div className="flex items-center gap-2">
            <MembershipStatusBadge value={member.status} />
            <MembershipAccess permissions={[MEMBERSHIP_PERMISSIONS.EDIT, MEMBERSHIP_PERMISSIONS.MANAGE]}>
              {!editing ? (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-black text-slate-700 hover:bg-slate-50"
                >
                  <Pencil className="h-4 w-4" />
                  Edit
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-black text-slate-700 hover:bg-slate-50"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </button>
              )}
            </MembershipAccess>
          </div>
        }
      />

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="grid gap-6 xl:col-span-2">

          {/* ── Profile section — view OR inline edit ────────────────── */}
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-black text-slate-900">Member Profile</h2>
                {editing && (
                  <p className="mt-0.5 text-xs text-slate-500">Required fields marked *.</p>
                )}
              </div>

              {editing && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="inline-flex items-center gap-1.5 rounded-2xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50"
                  >
                    <X className="h-3.5 w-3.5" />
                    Discard
                  </button>
                  <button
                    type="button"
                    onClick={saveEdit}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 rounded-2xl bg-emerald-700 px-3 py-2 text-xs font-black text-white hover:bg-emerald-800 disabled:opacity-60"
                  >
                    <Check className="h-3.5 w-3.5" />
                    {saving ? "Saving…" : "Save Changes"}
                  </button>
                </div>
              )}
            </div>

            {saveError && (
              <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
                {saveError}
              </div>
            )}

            {editing ? (
              /* ── EDIT MODE ─────────────────────────────────────────── */
              <div className="mt-5 grid gap-4 md:grid-cols-2">
                {/* Company Name */}
                <div className="md:col-span-2">
                  <EditField label="Company Name" required>
                    <input
                      value={form.companyName}
                      onChange={(e) => set("companyName", e.target.value)}
                      placeholder="Company name"
                      className={inputCls}
                    />
                  </EditField>
                </div>

                {/* Category */}
                <EditField label="Category" required>
                  <select
                    value={form.categoryId}
                    onChange={(e) =>
                      set("categoryId", e.target.value === "" ? "" : Number(e.target.value))
                    }
                    className={selectCls}
                  >
                    <option value="">Select…</option>
                    {(categoriesQuery.data ?? []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </EditField>

                {/* Membership Code */}
                <EditField label="Membership Code">
                  <input
                    value={form.membershipCode}
                    onChange={(e) => set("membershipCode", e.target.value)}
                    placeholder="e.g. EATTA-2025-0042"
                    className={inputCls}
                  />
                </EditField>

                {/* Source */}
                <EditField label="Application Source">
                  <select
                    value={form.source}
                    onChange={(e) => set("source", e.target.value as MembershipSource | "")}
                    className={selectCls}
                  >
                    <option value="">Select…</option>
                    {SOURCE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </EditField>

                {/* Status */}
                <EditField label="Membership Status">
                  <select
                    value={form.status}
                    onChange={(e) => set("status", e.target.value as MemberStatus | "")}
                    className={selectCls}
                  >
                    <option value="">Select…</option>
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </EditField>

                {/* Certificate Issued */}
                <EditField label="Certificate Issued">
                  <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 px-4 py-2.5 hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={form.certificateIssued}
                      onChange={(e) => set("certificateIssued", e.target.checked)}
                      className="h-4 w-4 rounded accent-emerald-700"
                    />
                    <span className="text-sm font-bold text-slate-700">
                      Certificate issued
                    </span>
                  </label>
                </EditField>

                {/* Notes */}
                <div className="md:col-span-2">
                  <EditField label="Internal Notes">
                    <textarea
                      value={form.notes}
                      onChange={(e) => set("notes", e.target.value)}
                      rows={3}
                      placeholder="Any internal notes…"
                      className={inputCls}
                    />
                  </EditField>
                </div>

                {/* Read-only workflow audit */}
                <div className="md:col-span-2 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <p className="mb-3 text-xs font-black uppercase tracking-wide text-slate-400">
                    Workflow Audit (read-only)
                  </p>
                  <div className="grid gap-2 text-xs md:grid-cols-3">
                    {[
                      { label: "Board Approval Date", value: member.boardApprovalDate },
                      { label: "Board Decision By", value: member.boardDecisionBy },
                      { label: "Board Notes", value: member.boardNotes },
                      { label: "Payment Confirmed At", value: member.paymentConfirmedAt },
                      { label: "Payment Confirmed By", value: member.paymentConfirmedBy },
                      { label: "ICT Completed At", value: member.ictCompletedAt },
                      { label: "ICT Completed By", value: member.ictCompletedBy },
                      { label: "Training Completed At", value: member.trainingCompletedAt },
                      { label: "Training Completed By", value: member.trainingCompletedBy },
                      { label: "Active Date", value: member.activeDate },
                      { label: "Certificate Issue Date", value: member.certificateIssueDate },
                      { label: "Auction Access", value: member.auctionAccessAllowed ? "Allowed" : "Not allowed" },
                    ].map(({ label, value }) => (
                      <div key={label}>
                        <p className="font-black uppercase tracking-wide text-slate-400">{label}</p>
                        <p className="mt-0.5 font-bold text-slate-700">{value ?? "—"}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              /* ── VIEW MODE ─────────────────────────────────────────── */
              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <Info label="Company Name" value={member.companyName} />
                <Info label="Category" value={member.categoryName} />
                <Info label="Membership Code" value={member.membershipCode ?? "—"} />
                <Info label="Source" value={member.source.replace(/_/g, " ")} />
                <Info label="Status" value={member.status.replace(/_/g, " ")} />
                <Info label="Workflow Stage" value={member.workflowStage?.replace(/_/g, " ") ?? "—"} />
                <Info label="Auction Access" value={member.auctionAccessAllowed ? "Allowed" : "Not Allowed"} />
                <Info label="Certificate Issued" value={member.certificateIssued ? "Yes" : "No"} />
                <Info label="Board Approval Date" value={member.boardApprovalDate ?? "—"} />
                <Info label="Board Decision By" value={member.boardDecisionBy ?? "—"} />
                <Info label="Payment Confirmed By" value={member.paymentConfirmedBy ?? "—"} />
                <Info label="Payment Confirmed At" value={member.paymentConfirmedAt ?? "—"} />
                <Info label="ICT Completed By" value={member.ictCompletedBy ?? "—"} />
                <Info label="ICT Completed At" value={member.ictCompletedAt ?? "—"} />
                <Info label="Training Completed By" value={member.trainingCompletedBy ?? "—"} />
                <Info label="Training Completed At" value={member.trainingCompletedAt ?? "—"} />
                <Info label="Active Date" value={member.activeDate ?? "—"} />
                <Info label="Certificate Issue Date" value={member.certificateIssueDate ?? "—"} />
                {member.notes && (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 md:col-span-2">
                    <p className="text-xs font-black uppercase tracking-wide text-slate-500">Notes</p>
                    <p className="mt-1 text-sm text-slate-700">{member.notes}</p>
                  </div>
                )}
              </div>
            )}
          </section>

          <MembershipApprovalPanel
            member={member}
            fees={feesQuery.data ?? []}
            ictAccess={ictQuery.data}
            feeTypes={feeTypesQuery.data ?? []}
            onReload={reload}
          />

          <MemberContactsPanel
            memberId={id}
            contacts={contactsQuery.data ?? []}
            onReload={reload}
          />

          <MembershipFinancePanel fees={feesQuery.data ?? []} onReload={reload} />
          <MembershipIctPanel memberId={id} access={ictQuery.data} onReload={reload} />
          <MembershipDocumentsPanel memberId={id} documents={documentsQuery.data ?? []} onReload={reload} />
          <MembershipTradeFollowUpPanel memberId={id} followUps={followUpsQuery.data ?? []} onReload={reload} />
        </div>

        <MembershipWorkflowTimeline member={member} />
      </div>
    </div>
  );
}

// ── Helper components ─────────────────────────────────────────────────────────

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-xs font-black uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 font-bold text-slate-900">{value}</p>
    </div>
  );
}

function EditField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-black uppercase tracking-wide text-slate-500">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
